const findOption = function(node, value)
{
	const opts = Array.from(node?.list?.options ?? []);
	if (!opts.length)
		return undefined;
	const v = String(value ?? '').trim();
	let matches = opts.filter(option => String(option.value).trim() === v || option.value == value);
	if (matches.length === 1)
		return matches[0];
	if (matches.length > 1)
		return undefined; // ids duplicados entre empresas — nunca pegar o 1º
	// wh:loc — mesmo par com string ligeiramente diferente ou option.value sem trim
	if (/^\d+:\d+$/.test(v))
	{
		const a = Number(v.split(':')[0]);
		const b = Number(v.split(':')[1]);
		matches = opts.filter(option =>
		{
			const p = String(option.value ?? '').trim();
			const m = /^(\d+):(\d+)$/.exec(p);
			return m && Number(m[1]) === a && Number(m[2]) === b;
		});
		if (matches.length === 1)
			return matches[0];
	}
	// company_id:id (filtro multi-empresa)
	if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:\d+$/i.test(v))
	{
		matches = opts.filter(option => String(option.value).trim().toLowerCase() === v.toLowerCase());
		if (matches.length === 1)
			return matches[0];
	}
	return undefined;
};

const normalizeForMatch = function(str)
{
	return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
};

/** Só dígitos — para casar CNPJ/CPF digitado com rótulo que traz documento mascarado. */
export const digitsOnly = function(str)
{
	return String(str || '').replace(/\D/g, '');
};

const listOptions = function(options)
{
	return Array.from(options ?? []);
};

/** Matches de documento (11/14 dígitos). Preferência: dígitos do rótulo iguais ao digitado; senão includes. */
const documentDigitMatches = function(options, rawTyped)
{
	const d = digitsOnly(String(rawTyped || '').trim());
	if (d.length !== 11 && d.length !== 14)
		return [];
	const opts = listOptions(options);
	const exact = opts.filter(o => digitsOnly(o.innerText || '') === d);
	if (exact.length)
		return exact;
	return opts.filter(o => digitsOnly(o.innerText || '').includes(d));
};

const partialDocumentDigitMatches = function(options, rawTyped, minLen = 8)
{
	const d = digitsOnly(String(rawTyped || '').trim());
	if (d.length < minLen || d.length === 11 || d.length === 14)
		return [];
	return listOptions(options).filter(o => digitsOnly(o.innerText || '').includes(d));
};

/** Quando o usuário digita 11 ou 14 dígitos (com ou sem máscara), encontra a option cujo rótulo contém esse documento — só se for inequívoco. */
export const findOptionByDocumentDigits = function(options, rawTyped)
{
	const matches = documentDigitMatches(options, rawTyped);
	return matches.length === 1 ? matches[0] : undefined;
};

/** Busca parcial por CNPJ/CPF (mín. 8 dígitos) — só se uma única option casar (evita raiz CNPJ pegar 1ª filial). */
export const findOptionByPartialDocumentDigits = function(options, rawTyped, minLen = 8)
{
	const matches = partialDocumentDigitMatches(options, rawTyped, minLen);
	return matches.length === 1 ? matches[0] : undefined;
};

const labelExactMatches = function(options, typed)
{
	const t = String(typed || '').trim();
	if (!t)
		return [];
	return listOptions(options).filter(o => o.innerText && o.innerText.trim() === t);
};

const labelNormalizedExactMatches = function(options, typed)
{
	const nTyped = normalizeForMatch(typed);
	if (!nTyped)
		return [];
	return listOptions(options).filter(o => o.innerText && normalizeForMatch(o.innerText) === nTyped);
};

const labelPrefixMatches = function(options, typed, caseSensitive)
{
	if (caseSensitive)
	{
		const t = String(typed).trim();
		return listOptions(options).filter(o => o.innerText && String(o.innerText).startsWith(t));
	}
	const nTyped = normalizeForMatch(typed);
	if (!nTyped)
		return [];
	return listOptions(options).filter(o => o.innerText && normalizeForMatch(o.innerText).startsWith(nTyped));
};

const labelIncludesMatches = function(options, typed, caseSensitive)
{
	if (caseSensitive)
	{
		const t = String(typed).trim();
		return listOptions(options).filter(o => o.innerText && String(o.innerText).includes(t));
	}
	const nTyped = normalizeForMatch(typed);
	if (!nTyped)
		return [];
	return listOptions(options).filter(o => o.innerText && normalizeForMatch(o.innerText).includes(nTyped));
};

const isValidId = function(val)
{
	if (val == null || val === '' || val === -1 || val === '-1') return false;
	const n = Number(val);
	return !isNaN(n) && n >= 0;
};

/** Valor composto `warehouse_id:location_id` (saídas multi-armazém — obs_load_options `compositeWhLoc`). */
const isCompositeWhLocValue = function(val)
{
	return /^\d+:\d+$/.test(String(val ?? '').trim());
};

/** `company_uuid:entity_id` — evita colisão de id entre empresas no datalist do filtro. */
const isCompanyEntityKey = function(val)
{
	return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:\d+$/i.test(String(val ?? '').trim());
};

/**
 * Desempate: preferred → único → (caseSensitive) menor rótulo inequívoco → undefined.
 * Não pega o “1º da lista” em homônimos do mesmo tamanho (ex.: mesmo nick de filial).
 */
const disambiguateByPreferred = function(candidates, preferredValue, caseSensitive = false)
{
	if (!candidates?.length)
		return undefined;
	if (candidates.length === 1)
		return candidates[0];
	if (isValidId(preferredValue) || isCompositeWhLocValue(preferredValue) || isCompanyEntityKey(preferredValue))
	{
		const pref = candidates.find(o => String(o.value) === String(preferredValue));
		if (pref)
			return pref;
	}
	// Case-sensitive (posições): entre vários prefixos (S-1, S-10), o menor rótulo único vence.
	if (caseSensitive)
	{
		const sorted = [...candidates].sort((a, b) =>
			String(a.innerText || '').length - String(b.innerText || '').length
			|| String(a.value).localeCompare(String(b.value)));
		const shortestLen = String(sorted[0].innerText || '').length;
		const sameLen = sorted.filter(o => String(o.innerText || '').length === shortestLen);
		if (sameLen.length === 1)
			return sameLen[0];
	}
	return undefined;
};

/**
 * Resolve texto digitado → option.
 * Ordem: id → rótulo exato → rótulo normalizado → CNPJ/CPF completo (único) →
 * prefixo → includes → CNPJ parcial.
 * Homônimos sem preferred → undefined (exceto caseSensitive com menor rótulo único).
 */
const resolveOptionFromTyped = function(options, rawTyped, preferredValue, caseSensitive = false)
{
	const typed = String(rawTyped || '').trim();
	if (!typed.length)
		return undefined;

	const byValue = listOptions(options).filter(o => String(o.value) === typed || o.value == typed);
	const fromValue = disambiguateByPreferred(byValue, preferredValue, caseSensitive);
	if (fromValue)
		return fromValue;

	const fromLabelExact = disambiguateByPreferred(labelExactMatches(options, typed), preferredValue, caseSensitive);
	if (fromLabelExact)
		return fromLabelExact;

	if (!caseSensitive)
	{
		const fromNorm = disambiguateByPreferred(labelNormalizedExactMatches(options, typed), preferredValue, caseSensitive);
		if (fromNorm)
			return fromNorm;
	}

	const fromDoc = disambiguateByPreferred(documentDigitMatches(options, typed), preferredValue, caseSensitive);
	if (fromDoc)
		return fromDoc;

	const fromPrefix = disambiguateByPreferred(labelPrefixMatches(options, typed, caseSensitive), preferredValue, caseSensitive);
	if (fromPrefix)
		return fromPrefix;

	const fromIncludes = disambiguateByPreferred(labelIncludesMatches(options, typed, caseSensitive), preferredValue, caseSensitive);
	if (fromIncludes)
		return fromIncludes;

	return disambiguateByPreferred(partialDocumentDigitMatches(options, typed), preferredValue, caseSensitive);
};

const setOption = function(node, callback)
{
	let options = Array.from(node?.list?.options ?? []);
	let option = options.find(callback);
	// -2: invalid, -1: empty. Opcional + sem match: se lista vazia (ex: produto sem estoque), manter texto digitado (-2); senão tratar como vazio (-1)
	let new_value = option?.value ?? (node.value.length ? (this.isRequired() ? -2 : (options.length === 0 ? -2 : -1)) : -1);
	let old_value = this.getValue() ?? -1;
	// Input vazio e campo tem id: só preservar se opções ainda não carregaram (não há option para old_value). Se há option, usuário pode ter limpado de propósito.
	if (new_value === -1 && !node.value.length && (isValidId(old_value) || isCompositeWhLocValue(old_value) || isCompanyEntityKey(old_value)) && !findOption(node, old_value))
		return void this.refresh();
	if (new_value != old_value)
	{
		this.setValue(new_value);
		this.checkErrorState();
	}
	this.refresh();		// Must be out here or it won't show properly when you clear the field and retype the old value
};

export default function (...args)
{
	const caseSensitive = args[0]?.caseSensitive === true;

	return {

		_caseSensitive: caseSensitive,
		events: ['input', 'blur'],

		checkValue(value = this.getValue())
		{
			let node = document.querySelector(this.selector);
			if (findOption(node, value))
				return true;
			// Campo opcional com id numérico válido: aceitar mesmo sem opção (ex.: lista ainda não carregou)
			if (!this.isRequired() && isValidId(value))
				return true;
			// `warehouse_id:location_id` (datalist de posições na saída): válido antes da lista IndexedDB terminar de popular
			if (isCompositeWhLocValue(value))
				return true;
			if (isCompanyEntityKey(value))
				return true;
			// Campo opcional com valor inválido (-2): tratar como vazio para permitir avançar (usuário digitou algo que não está na lista)
			if (!this.isRequired() && (value == -2 || value == '-2'))
				return true;
			return false;
		},

		isEmpty(value = this.getValue())
		{
			// Não usar !value: device_id 0 é válido; só null/undefined/''/-1 são vazios
			return value == null || value === '' || value == -1 || value === '-1';
		},

		refresh()
		{
			let node = document.querySelector(this.selector);
			if (!node || !node.list)
				return;
			let value = this.getValue();
			if (this.isEmpty(value))
			{
				node.value = '';
				return;
			}
			let option = findOption(node, value);
			if (option)
				node.value = option.innerText;
			else if (value != -2)
			{
				// Opções ainda não carregaram: manter id (ou composto wh:loc / company:id) como fallback em vez de vazio
				const s = String(value ?? '').trim();
				const displayed = String(node.value ?? '').trim();
				if (isCompositeWhLocValue(s) || isCompanyEntityKey(s))
					node.value = s;
				else if (isValidId(value))
					node.value = String(value);
				else
					node.value = displayed;
			}
		},

		handleEvents(ev)
		{
			switch (ev.type)
			{
				case 'input':														// Event generated when user select an option from the dropdown list
					if (!ev.inputType || ev.inputType == 'insertReplacementText')	// React to list selections only
					{
						const options = Array.from(ev.target?.list?.options ?? []);
						const raw = String(ev.target.value).trim();
						const prev = this.getValue();
						const option = resolveOptionFromTyped(options, raw, prev, caseSensitive);
						setOption.call(this, ev.target, (o) => o === option);
					}
					break;
				case 'blur':
				{
					let node = document.querySelector(this.selector);
					if (node.value != findOption(node, this.getValue())?.innerText)
					{
						let options = Array.from(ev.target?.list?.options ?? []);
						let resolved = resolveOptionFromTyped(options, ev.target.value, this.getValue(), caseSensitive);
						setOption.call(this, ev.target, (option) => option === resolved);
					}
					break;
				}
			}
		}

	};
}
